import apiClient from "./client";
import { clientGet } from "./clientApi";
import { trackMarketingEvent } from "@/lib/tracking/marketing-events";

function getBrowserLocale(): "ar" | "en" {
  return document.documentElement.lang.toLowerCase().startsWith("en")
    ? "en"
    : "ar";
}
import type {
  ApiResponse,
  PaginatedResponse,
  Project,
  Category,
  Association,
  PaymentMethod,
  City,
  Bank,
  BlogPost,
  NewsArticle,
  FAQ,
  ContactMessagePayload,
  VolunteerApplication,
  ProjectRequestPayload,
  DashboardStats,
  PortalMenuItem,
  User,
  LoginRequest,
  RegisterRequest,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  AuthResponse,
  AuthSessionKind,
  AuthLoginApiData,
  AssociationSocialMediaConfigData,
  // New donation system types
  ProcessCartResponse,
  PayOneProjectRequest,
  PaymentStatusResponse,
  PaymentVerificationResponse,
  DonationRatingRequest,
  DonationRatingResponse,
  PaymentGatewayConfig,
  MoyasarInitiateRequest,
  MoyasarInitiateResponse,
  MoyasarUpdateRequest,
  MyFatoorahInitiateSessionRequest,
  MyFatoorahInitiateSessionResponse,
  MyFatoorahExecuteRequest,
  MyFatoorahExecuteResponse,
  HyperPayCheckoutRequest,
  HyperPayCheckoutResponse,
  StripeCreateSessionRequest,
  StripeCreateSessionResponse,
  BankWithLogo,
  BankAccountDetails,
  BankWithAccounts,
  ReceiptUploadResponse,
  GiftVoucher,
  SentGift,
  Certificate,
  CertificateProject,
  BillDeduction,
  DeductionSubscription,
  ShippingCity,
  ShippingAddress,
} from "@/types";
import { normalizeBanksWithAccounts } from "@/lib/payments/bankTransfer";
import type { DonationSuccessSettings } from "@/lib/donationSuccessSettings";

// NOTE: Caching migrated to serverServices + serverCache (unstable_cache).
// Axios (apiClient) retained for: auth, payments, mutations, file uploads.
// Client data reads should use clientGet -> /api/* routes.

// Association Services
export const associationService = {
  getByDomain: async (
    domain: string,
  ): Promise<ApiResponse<{ association: Association }>> => {
    return apiClient.get(`/associations/by-domain`, { params: { domain } });
  },
  getById: async (
    id: number,
  ): Promise<ApiResponse<{ association: Association }>> => {
    return apiClient.get(`/associations/${id}`);
  },
  getSocialMediaConfig: async (
    id: number,
  ): Promise<ApiResponse<AssociationSocialMediaConfigData>> => {
    return apiClient.get(`/associations/${id}/social-media-config`);
  },
  getBankAccounts: async (id: number): Promise<ApiResponse> => {
    return apiClient.get(`/associations/${id}/bank-accounts`);
  },
  savePortalTheme: async (
    id: number,
    config: Record<string, unknown>
  ): Promise<ApiResponse> => {
    void id;
    const resp = await fetch("/api/admin-editor/theme", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ config }),
    });
    let json: ApiResponse & { code?: string } = {
      success: false,
      message: "Failed to save theme",
    };
    try {
      json = (await resp.json()) as ApiResponse & { code?: string };
    } catch {
      /* non-JSON body */
    }
    if (!resp.ok || json.success === false) {
      const code =
        typeof json.code === "string"
          ? json.code
          : resp.status === 401
            ? "session-expired"
            : "save-failed";
      const err = new Error(json.message || "Failed to save theme") as Error & {
        status?: number;
        code?: string;
      };
      err.status = resp.status;
      err.code = code;
      throw err;
    }
    return json;
  },
  deletePortalTheme: async (id: number): Promise<ApiResponse> => {
    return apiClient.put(`/associations/${id}/portal-theme`, { config: {} });
  },
};

// Project Services
export const projectService = {
  getAll: async (params?: {
    page?: number;
    pageSize?: number;
    associationId?: number;
    categoryId?: number;
    donateTypeId?: number;
    search?: string;
    showInHomePage?: boolean;
    isActive?: boolean;
    isProduct?: boolean;
    giftProject?: boolean;
    globalGift?: boolean;
    hideFromStore?: boolean;
  }): Promise<PaginatedResponse<Project>> => {
    if (typeof window !== "undefined") {
      return clientGet<PaginatedResponse<Project>>("/api/projects", {
        ...params,
        locale: getBrowserLocale(),
      });
    }
    return apiClient.get("/projects", { params });
  },
  getById: async (id: number): Promise<ApiResponse<{ project: Project }>> => {
    if (typeof window !== "undefined") {
      return clientGet<ApiResponse<{ project: Project }>>(
        `/api/projects/${id}`,
        { locale: getBrowserLocale() },
      );
    }
    return apiClient.get(`/projects/${id}`);
  },
  getFeatured: async (
    associationId?: number,
  ): Promise<ApiResponse<Project[]>> => {
    return apiClient.get("/projects/featured", {
      params: { associationId },
    });
  },
  getShares: async (id: number): Promise<ApiResponse> => {
    if (typeof window !== "undefined") {
      return clientGet<ApiResponse>(`/api/projects/${id}/shares`);
    }
    return apiClient.get(`/projects/${id}/shares`);
  },
  /** Get published comments for a project (portal). */
  getComments: async (
    projectId: number,
    associationId?: number,
  ): Promise<
    ApiResponse<
      {
        id: number;
        message: string;
        messageReply: string | null;
        createDate: string | null;
        updateDate: string | null;
        authorName: string;
      }[]
    >
  > => {
    return apiClient.get(`/projects/${projectId}/comments`, {
      headers:
        typeof associationId === "number" && associationId > 0
          ? { "X-Association-Id": String(associationId) }
          : undefined,
    });
  },
  /** Add a comment (guest or authenticated). */
  addComment: async (
    projectId: number,
    message: string,
    associationId?: number,
  ): Promise<ApiResponse<{ id: number }>> => {
    return apiClient.post(
      `/projects/${projectId}/comments`,
      { message },
      {
        headers:
          typeof associationId === "number" && associationId > 0
            ? { "X-Association-Id": String(associationId) }
            : undefined,
      },
    );
  },
};

// Category Services
export const categoryService = {
  getAll: async (
    associationId?: number,
    viewHomePage?: boolean,
    forDeductions?: boolean,
    viewQuickDonation?: boolean,
    viewInCampaigns?: boolean,
  ): Promise<ApiResponse<Category[]>> => {
    if (typeof window !== "undefined") {
      return clientGet<ApiResponse<Category[]>>("/api/categories", {
        associationId,
        viewHomePage: viewHomePage ? "true" : undefined,
        forDeductions: forDeductions ? "1" : undefined,
        viewQuickDonation: viewQuickDonation ? "true" : undefined,
        viewInCampaigns: viewInCampaigns ? "true" : undefined,
        locale: getBrowserLocale(),
      });
    }
    return apiClient.get("/categories", {
      params: {
        associationId,
        viewHomePage: viewHomePage ? "true" : undefined,
        forDeductions: forDeductions ? "1" : undefined,
        viewQuickDonation: viewQuickDonation ? "true" : undefined,
        viewInCampaigns: viewInCampaigns ? "true" : undefined,
      },
    });
  },
  getById: async (id: number): Promise<ApiResponse<{ category: Category }>> => {
    return apiClient.get(`/categories/${id}`);
  },
};

// Attachments Services
export const attachmentService = {
  getMetadata: async (
    id: number | string,
  ): Promise<ApiResponse<{ Extension?: string | null }>> => {
    return apiClient.get(`/attachments/metadata/${id}`);
  },
};

// Gift Services (merged)
export const giftService = {
  getAttributes: async (projectId: number): Promise<ApiResponse> => {
    return apiClient.get(`/gifts/attributes/${projectId}`);
  },

  createRecipient: async (data: {
    sendToName: string;
    sendToMobile: string;
    senderName: string;
    message?: string;
  }): Promise<ApiResponse> => {
    return apiClient.post("/gifts/recipients", data);
  },

  getRecipients: async (
    donationOperationDetailId: number,
  ): Promise<ApiResponse> => {
    return apiClient.get(`/gifts/recipients/${donationOperationDetailId}`);
  },

  // Get gift voucher by ID (public endpoint)
  getVoucher: async (
    giftId: number | string,
  ): Promise<ApiResponse<{ gift: GiftVoucher }>> => {
    return apiClient.get(`/gifts/${giftId}`);
  },

  // Get authenticated user's sent gifts
  getMyGifts: async (
    associationId: number,
  ): Promise<ApiResponse<{ gifts: SentGift[] }>> => {
    return apiClient.get("/gifts/my-gifts", { params: { associationId } });
  },

  // Resend gift SMS
  resend: async (giftId: number | string): Promise<ApiResponse> => {
    return apiClient.post(`/gifts/${giftId}/resend`);
  },
};

// SMS Services
export const smsService = {
  getInstructions: async (associationId?: number): Promise<ApiResponse> => {
    return apiClient.get("/sms/instructions", {
      params: { associationId },
    });
  },
  getDonations: async (associationId: number): Promise<ApiResponse> => {
    return apiClient.get(
      `/association-extensions/${associationId}/sms-donations`,
    );
  },
};

// Partners Services
export const partnersService = {
  getAll: async (associationId: number): Promise<ApiResponse> => {
    return apiClient.get(`/association-extensions/${associationId}/partners`);
  },
};

// Testimonials Services
export const testimonialService = {
  getAll: async (associationId: number): Promise<ApiResponse> => {
    return apiClient.get(
      `/association-extensions/${associationId}/testimonials`,
    );
  },
};

// Billing Services
export const billingService = {
  createRecurring: async (data: {
    projectId: number;
    amount: number;
    billsDate: Date;
    projectShareId?: number;
    quantity?: number;
  }): Promise<ApiResponse> => {
    return apiClient.post("/billing/recurring", data);
  },

  // Get user's bill deductions (subscriptions)
  getSubscriptions: async (): Promise<
    ApiResponse<DeductionSubscription[]>
  > => {
    return apiClient.get("/billing/subscriptions");
  },

  getBills: async (params?: {
    isPaid?: boolean;
    statusVal?: 1 | 2;
    groupId?: number;
    startDate?: string;
    endDate?: string;
    page?: number;
    pageSize?: number;
  }): Promise<ApiResponse<{ bills: BillDeduction[] }>> => {
    return apiClient.get("/billing/bills", {
      params,
    });
  },

  // Cancel a subscription group
  cancelGroup: async (groupId: number): Promise<ApiResponse> => {
    return apiClient.delete(`/billing/groups/${groupId}/cancel`);
  },

  setGroupStopped: async (
    groupId: number,
    isStopped: boolean,
  ): Promise<ApiResponse> => {
    return apiClient.put(`/billing/groups/${groupId}/stopped`, { isStopped });
  },
};

// Lookup Services
export const lookupService = {
  getPaymentMethods: async (): Promise<ApiResponse<PaymentMethod[]>> => {
    return apiClient.get("/lookups/payment-methods");
  },
  getCities: async (): Promise<ApiResponse<City[]>> => {
    return apiClient.get("/lookups/cities");
  },
  getBanks: async (): Promise<ApiResponse<Bank[]>> => {
    return apiClient.get("/lookups/banks");
  },
  getContactMessageTypes: async (): Promise<ApiResponse> => {
    return apiClient.get("/lookups/contact-message-types");
  },
  getHomePageSections: async (): Promise<ApiResponse> => {
    return apiClient.get("/lookups/home-page-sections");
  },
};

// Content Services
export const contentService = {
  getBlogs: async (params?: {
    page?: number;
    pageSize?: number;
    associationId?: number;
  }): Promise<PaginatedResponse<BlogPost>> => {
    return apiClient.get("/content/blog", { params });
  },
  getBlogById: async (id: number): Promise<ApiResponse<{ blog: BlogPost }>> => {
    return apiClient.get(`/content/blog/${id}`);
  },
  getNews: async (params?: {
    page?: number;
    pageSize?: number;
    associationId?: number;
    // Optional filters (backend may support these)
    search?: string;
    newsTypeId?: number;
  }): Promise<PaginatedResponse<NewsArticle>> => {
    return apiClient.get("/content/news", { params });
  },
  getNewsById: async (
    id: number,
  ): Promise<ApiResponse<{ news: NewsArticle }>> => {
    return apiClient.get(`/content/news/${id}`);
  },
  getFAQs: async (associationId?: number): Promise<ApiResponse<FAQ[]>> => {
    return apiClient.get("/content/faq", {
      params: { associationId },
    });
  },
  getPortalPages: async (associationId: number): Promise<ApiResponse> => {
    return apiClient.get("/content/portal-pages", {
      params: { associationId },
    });
  },
  getPortalMenus: async (
    associationId: number,
  ): Promise<
    ApiResponse<{ topMenu: PortalMenuItem[]; footerMenu: PortalMenuItem[] }>
  > => {
    return apiClient.get("/content/portal-menus", {
      params: { associationId },
    });
  },
  getPortalPageBySlug: async (
    slug: string,
    associationId: number,
  ): Promise<ApiResponse> => {
    // Clean slug: remove leading/trailing slashes and whitespace
    const cleanSlug = slug.trim().replace(/^\/+|\/+$/g, "");

    // IMPORTANT:
    // Slugs can contain "/" (e.g. "Association/Licenses"). If we interpolate that
    // directly into the path, it becomes multiple URL segments and breaks the API route.
    // Encode the slug so "/" becomes "%2F" and remains a single path parameter.
    let decodedSlug = cleanSlug;
    try {
      decodedSlug = decodeURIComponent(cleanSlug);
    } catch {
      // ignore decode errors; treat input as already-decoded
    }
    const encodedSlug = encodeURIComponent(decodedSlug);

    return apiClient.get(`/content/portal-pages/slug/${encodedSlug}`, {
      params: { associationId },
    });
  },
  getPortalPageById: async (id: string | number): Promise<ApiResponse> => {
    return apiClient.get(`/content/portal-pages/${id}`);
  },
  getHomePageContent: async (associationId: number): Promise<ApiResponse> => {
    return apiClient.get("/content/portal-pages", {
      params: { associationId },
    });
  },
};

// Contact, Volunteer & Project Request Services
export const formService = {
  uploadContactImage: async (
    file: File,
  ): Promise<ApiResponse<{ imageId: number; imageUrl?: string }>> => {
    const formData = new FormData();
    formData.append("file", file);
    return apiClient.post("/contact/upload-image", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  submitContact: async (data: ContactMessagePayload): Promise<ApiResponse> => {
    return apiClient.post("/contact/messages", data);
  },
  submitVolunteer: async (data: VolunteerApplication): Promise<ApiResponse> => {
    return apiClient.post("/volunteer/applications", data);
  },
  submitProjectRequest: async (
    data: ProjectRequestPayload,
  ): Promise<ApiResponse<{ id?: number; reference?: string }>> => {
    // Uses the same-origin Next BFF (keeps the ERP internal URL off the browser
    // and forwards association context / auth cookies server-side).
    const res = await fetch("/api/project-requests", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    const json = (await res.json().catch(() => ({}))) as ApiResponse<{
      id?: number;
      reference?: string;
    }>;
    if (!res.ok || json.success === false) {
      throw new Error(json.message || "Failed to submit project request");
    }
    return json;
  },
  sendProjectRequestOtp: async (data: {
    mobile: string;
    name?: string;
  }): Promise<ApiResponse<{ expiresInSeconds?: number }>> => {
    const res = await fetch("/api/project-requests/otp/send", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    const json = (await res.json().catch(() => ({}))) as ApiResponse<{
      expiresInSeconds?: number;
    }>;
    if (!res.ok || json.success === false) {
      throw new Error(json.message || "Failed to send verification code");
    }
    return json;
  },
  verifyProjectRequestOtp: async (data: {
    mobile: string;
    code: string;
  }): Promise<
    ApiResponse<{ verificationToken: string; expiresInSeconds?: number }>
  > => {
    const res = await fetch("/api/project-requests/otp/verify", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    const json = (await res.json().catch(() => ({}))) as ApiResponse<{
      verificationToken: string;
      expiresInSeconds?: number;
    }>;
    if (!res.ok || json.success === false) {
      throw new Error(json.message || "Invalid verification code");
    }
    return json;
  },
};

// Payment Services
export const paymentService = {
  // Get payment methods
  getMethods: async (): Promise<
    ApiResponse<{ methods: { Id: number; Name: string; NameAr: string }[] }>
  > => {
    return apiClient.get("/payments/methods");
  },

  // Get payment gateway configuration for an association
  getGatewayConfig: async (
    associationId: number,
  ): Promise<ApiResponse<PaymentGatewayConfig>> => {
    return apiClient.get(`/payments/gateway/${associationId}`);
  },

  getDonationSuccessConfig: async (
    associationId: number,
  ): Promise<ApiResponse<DonationSuccessSettings>> => {
    return apiClient.get(`/portal/donation-success/config/${associationId}`);
  },

  // Quick checkout for a single project (supports guest donations)
  payOneProject: async (
    data: PayOneProjectRequest,
  ): Promise<ApiResponse<ProcessCartResponse>> => {
    const res = await fetch("/api/payments/pay-one-project", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    const json = (await res.json().catch(() => ({}))) as ApiResponse<ProcessCartResponse>;
    if (!res.ok && json.success !== false) {
      return { success: false, message: "Failed to create donation" };
    }
    return json;
  },

  // Get donation status
  getStatus: async (
    operationCode: string,
  ): Promise<ApiResponse<PaymentStatusResponse>> => {
    return apiClient.get(`/payments/status/${operationCode}`);
  },

  // Verify payment completion
  verify: async (
    operationCode: string,
  ): Promise<ApiResponse<PaymentVerificationResponse>> => {
    return apiClient.get(`/payments/verify/${operationCode}`);
  },

  getDonationReceipt: async (operationCode: string): Promise<ApiResponse> => {
    const res = await fetch(
      `/api/payments/receipt/${encodeURIComponent(operationCode)}`,
    );
    const json = (await res.json().catch(() => ({}))) as ApiResponse;
    if (!res.ok && json.success !== false) {
      return { success: false, message: "Failed to load receipt" };
    }
    return json;
  },

  submitRating: async (
    data: DonationRatingRequest,
  ): Promise<ApiResponse<DonationRatingResponse>> => {
    return apiClient.post("/payments/rating", data);
  },

  // Save shipping address for a donation
  saveShippingAddress: async (
    operationCode: string,
    data: ShippingAddress,
  ): Promise<ApiResponse> => {
    return apiClient.post(
      `/payments/donation/${operationCode}/shipping-address`,
      data,
    );
  },

  // ==========================================
  // Moyasar Gateway
  // ==========================================
  moyasar: {
    initiate: async (
      data: MoyasarInitiateRequest,
    ): Promise<ApiResponse<MoyasarInitiateResponse>> => {
      return apiClient.post("/payments/moyasar/initiate", data);
    },

    update: async (data: MoyasarUpdateRequest): Promise<ApiResponse> => {
      return apiClient.post("/payments/moyasar/update", data);
    },
  },

  // ==========================================
  // MyFatoorah Gateway
  // ==========================================
  myfatoorah: {
    initiateSession: async (
      data: MyFatoorahInitiateSessionRequest,
    ): Promise<ApiResponse<MyFatoorahInitiateSessionResponse>> => {
      return apiClient.post("/payments/myfatoorah/initiate-session", data);
    },

    execute: async (
      data: MyFatoorahExecuteRequest,
    ): Promise<ApiResponse<MyFatoorahExecuteResponse>> => {
      return apiClient.post("/payments/myfatoorah/execute", data);
    },
  },

  // ==========================================
  // HyperPay Gateway
  // ==========================================
  hyperpay: {
    checkout: async (
      data: HyperPayCheckoutRequest,
    ): Promise<ApiResponse<HyperPayCheckoutResponse>> => {
      return apiClient.post("/payments/hyperpay/checkout", data);
    },
  },

  // ==========================================
  // Stripe Gateway
  // ==========================================
  stripe: {
    createSession: async (
      data: StripeCreateSessionRequest,
    ): Promise<ApiResponse<StripeCreateSessionResponse>> => {
      return apiClient.post("/payments/stripe/create-session", data);
    },
  },
};

// Statistics Services
export const statsService = {
  getDashboard: async (
    associationId?: number,
  ): Promise<ApiResponse<{ stats: DashboardStats }>> => {
    return apiClient.get("/reports/dashboard", {
      params: { associationId },
    });
  },
};

// Recommended Projects Services
export const recommendedService = {
  getRecommended: async (
    associationId: number,
  ): Promise<ApiResponse<Project[]>> => {
    return apiClient.get(`/recommended/${associationId}`);
  },
};

// Homepage Services (use serverServices for cached reads)
export const homepageService = {
  getSettings: async (associationId: number): Promise<ApiResponse> => {
    return apiClient.get("/homepage/settings", { params: { associationId } });
  },
  getSections: async (associationId: number): Promise<ApiResponse> => {
    return apiClient.get("/homepage/sections", { params: { associationId } });
  },
  getHeroSlides: async (associationId: number): Promise<ApiResponse> => {
    return apiClient.get(`/association-extensions/${associationId}/carousel`);
  },
};

/** Parse expiresIn from API (`"1h"`, `"3600"`, or seconds number). */
function parseAuthExpiresIn(
  raw: number | string | undefined,
): number | undefined {
  if (raw == null) return undefined;
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  if (typeof raw !== "string") return undefined;
  const s = raw.trim();
  if (/^\d+$/.test(s)) return Number.parseInt(s, 10);
  const h = s.match(/^(\d+(?:\.\d+)?)\s*h(?:our)?s?$/i);
  if (h) return Math.round(Number.parseFloat(h[1]) * 3600);
  const m = s.match(/^(\d+(?:\.\d+)?)\s*m(?:in(?:ute)?s?)?$/i);
  if (m) return Math.round(Number.parseFloat(m[1]) * 60);
  const d = s.match(/^(\d+(?:\.\d+)?)\s*d(?:ay)?s?$/i);
  if (d) return Math.round(Number.parseFloat(d[1]) * 86400);
  return undefined;
}

/**
 * Maps backend login payloads (nested `data.tokens` or flat JWT fields) to AuthResponse.
 */
export function normalizeAuthLoginPayload(data: unknown): AuthResponse | null {
  if (!data || typeof data !== "object") return null;
  const d = data as AuthLoginApiData & { authSessionKind?: unknown };
  const t = d.tokens;
  const accessToken = d.accessToken ?? t?.accessToken;
  if (!accessToken) return null;
  const refreshToken = d.refreshToken ?? t?.refreshToken;
  const user = d.user;
  const expiresIn = parseAuthExpiresIn(d.expiresIn ?? t?.expiresIn);
  const k = d.authSessionKind;
  const authSessionKind: AuthSessionKind | undefined =
    k === "fast" || k === "full" ? k : undefined;
  return { accessToken, refreshToken, user, expiresIn, authSessionKind };
}

// Authentication Services
export const authService = {
  login: async (data: LoginRequest): Promise<ApiResponse<AuthResponse>> => {
    const response = (await apiClient.post("/auth/login", {
      username: data.username,
      password: data.password,
    })) as ApiResponse<unknown>;

    if (response.success && response.data) {
      const normalized = normalizeAuthLoginPayload(response.data);
      if (normalized) {
        trackMarketingEvent("LOGIN", { method: "password" });
        return {
          ...response,
          data: normalized,
        } as ApiResponse<AuthResponse>;
      }
      return {
        success: false,
        message: response.message || "Login failed",
        error: response.error || "Missing access token in login response",
      } as ApiResponse<AuthResponse>;
    }

    return response as ApiResponse<AuthResponse>;
  },

  loginFaster: async (mobile: string): Promise<ApiResponse<AuthResponse>> => {
    const response = (await apiClient.post("/auth/login-faster", {
      mobile: mobile,
    })) as ApiResponse<unknown>;

    if (response.success && response.data) {
      const normalized = normalizeAuthLoginPayload(response.data);
      if (normalized) {
        trackMarketingEvent("LOGIN", { method: "fast" });
        return {
          ...response,
          data: {
            ...normalized,
            authSessionKind: "fast",
          },
        } as ApiResponse<AuthResponse>;
      }
      return {
        success: false,
        message: response.message || "Login failed",
        error: response.error || "Missing access token in login response",
      } as ApiResponse<AuthResponse>;
    }

    return response as ApiResponse<AuthResponse>;
  },

  // Check if a phone number exists in the system (legacy)
  checkPhoneExists: async (
    mobile: string,
  ): Promise<ApiResponse<{ exists: boolean }>> => {
    try {
      const response = await apiClient.post("/auth/check-phone", {
        mobile: mobile,
      });
      return response as unknown as ApiResponse<{ exists: boolean }>;
    } catch {
      // If the endpoint doesn't exist or fails, assume phone doesn't exist
      return {
        success: true,
        data: { exists: false },
      };
    }
  },

  // Check mobile and determine action: login, register, or set-password
  checkMobile: async (
    mobile: string,
  ): Promise<
    ApiResponse<{ action: "login" | "register" | "set-password" }>
  > => {
    try {
      const response = (await apiClient.post("/auth/check-mobile", {
        mobile: mobile,
      })) as unknown as ApiResponse<{
        action: "login" | "register" | "set-password";
      }>;
      return response;
    } catch {
      return {
        success: false,
        message: "Unable to check mobile number",
      };
    }
  },

  // Set password for existing user (faster user who needs to create password)
  setPassword: async (data: {
    mobile: string;
    password: string;
  }): Promise<ApiResponse<AuthResponse>> => {
    const response = (await apiClient.post("/auth/set-password", {
      mobile: data.mobile,
      password: data.password,
    })) as ApiResponse<unknown>;

    if (response.success && response.data) {
      const normalized = normalizeAuthLoginPayload(response.data);
      if (normalized) {
        return {
          ...response,
          data: normalized,
        } as ApiResponse<AuthResponse>;
      }
      return {
        success: false,
        message: response.message || "Failed to set password",
        error: response.error || "Missing access token in response",
      } as ApiResponse<AuthResponse>;
    }

    return response as ApiResponse<AuthResponse>;
  },

  register: async (
    data: RegisterRequest,
  ): Promise<ApiResponse<AuthResponse>> => {
    const response = (await apiClient.post("/auth/register", {
      mobile: data.mobile,
      password: data.password,
      name: data.name,
      email: data.email,
      associationId: data.associationId,
      // Note: ConfirmPassword is validated client-side only, not sent to API
    })) as ApiResponse<unknown>;

    if (response.success && response.data) {
      const normalized = normalizeAuthLoginPayload(response.data);
      if (normalized) {
        trackMarketingEvent("REGISTER");
        return {
          ...response,
          data: normalized,
        } as ApiResponse<AuthResponse>;
      }
    }

    // Return error response with proper type
    return {
      success: false,
      message: response.message || "Registration failed",
      error: response.error,
    } as ApiResponse<AuthResponse>;
  },

  forgotPassword: async (
    data: ForgotPasswordRequest,
  ): Promise<ApiResponse<{ Link: string }>> => {
    return apiClient.post("/auth/forgot-password", {
      mobile: data.mobile,
    });
  },

  resetPassword: async (data: ResetPasswordRequest): Promise<ApiResponse> => {
    return apiClient.post("/auth/reset-password", {
      resetCode: data.code,
      mobile: data.mobile,
      newPassword: data.password,
    });
  },

  changePassword: async (data: {
    currentPassword: string;
    newPassword: string;
  }): Promise<ApiResponse> => {
    return apiClient.post("/auth/change-password", {
      currentPassword: data.currentPassword,
      newPassword: data.newPassword,
    });
  },

  logout: async (): Promise<void> => {
    try {
      await apiClient.post("/auth/logout");
    } catch (error) {
      console.error("Logout API error:", error);
    }
    // Cookies are cleared by auth store
  },

  getCurrentUser: async (): Promise<ApiResponse<{ user: User }>> => {
    return apiClient.get("/auth/me");
  },

  refreshToken: async (): Promise<ApiResponse<AuthResponse>> => {
    // BFF model: refresh is done server-side using httpOnly cookies.
    const response = (await apiClient.post(
      "/auth/refresh",
      {},
    )) as ApiResponse<AuthResponse>;

    // Tokens are updated in cookies via auth store or client interceptor
    // No need to update localStorage here

    return response;
  },
};

export const userService = {
  getProfile: async (): Promise<ApiResponse> => {
    return apiClient.get("/users/profile");
  },
  updateProfile: async (data: {
    name?: string;
    email?: string;
    mobile?: string;
  }): Promise<ApiResponse> => {
    return apiClient.put("/users/profile", data);
  },

  // Get current user's profile stats (donation count, total amount, projects)
  // Backend endpoint: GET /users/me/stats
  getProfileStats: async (): Promise<
    ApiResponse<{
      donationOperationsCount: number;
      totalDonationAmount: number;
      casesAndProjectsCount: number;
    }>
  > => {
    return apiClient.get("/users/me/stats");
  },

  // Get current user's donation history
  // Backend endpoint: GET /users/me/donations
  getDonations: async (params?: {
    page?: number;
    pageSize?: number;
  }): Promise<ApiResponse> => {
    return apiClient.get("/users/me/donations", { params });
  },

  // Get donation receipt by operation code
  // Backend endpoint: GET /users/donations/:operationCode/receipt
  getDonationReceipt: async (operationCode: string): Promise<ApiResponse> => {
    return apiClient.get(`/users/donations/${operationCode}/receipt`);
  },

  // Get current user's alerts/notifications
  getAlerts: async (params?: {
    page?: number;
    pageSize?: number;
    markAsRead?: boolean;
  }): Promise<ApiResponse> => {
    return apiClient.get("/users/me/alerts", { params });
  },

  // Get unread alerts count
  getUnreadAlertsCount: async (): Promise<ApiResponse<{ count: number }>> => {
    return apiClient.get("/users/me/alerts/unread-count");
  },

  getMyContactMessages: async (params?: {
    page?: number;
    pageSize?: number;
  }): Promise<ApiResponse> => {
    return apiClient.get("/users/me/contact-messages", { params });
  },
};

// ==========================================
// Bank Services (for bank transfers)
// ==========================================
export const bankService = {
  // Get all banks
  getBanks: async (): Promise<ApiResponse<{ banks: BankWithLogo[] }>> => {
    return apiClient.get("/banks");
  },

  // Get bank accounts for a specific bank
  getBankAccounts: async (
    bankId: number,
    associationId: number,
  ): Promise<ApiResponse<{ accounts: BankAccountDetails[] }>> => {
    return apiClient.get(`/banks/${bankId}/accounts`, {
      params: { associationId },
    });
  },

  // Get all banks with their accounts
  getBanksWithAccounts: async (
    associationId: number,
  ): Promise<ApiResponse<{ banks: BankWithAccounts[] }>> => {
    const response = (await apiClient.get("/banks/with-accounts", {
      params: { associationId },
    })) as ApiResponse<{ banks?: unknown }>;

    const banks = normalizeBanksWithAccounts(response);
    return {
      ...response,
      success: response.success,
      data: { banks },
    };
  },

  // Upload receipt image (goes through Next.js API route which proxies to backend)
  uploadReceipt: async (
    file: File,
  ): Promise<ApiResponse<ReceiptUploadResponse>> => {
    const formData = new FormData();
    formData.append("file", file);
    const res = await fetch("/api/banks/receipt", {
      method: "POST",
      body: formData,
    });
    return res.json();
  },
};

// ==========================================
// Certificate Services
// ==========================================
export const certificateService = {
  // Get certificate by detail ID
  getByDetailId: async (
    detailId: number | string,
  ): Promise<ApiResponse<{ certificate: Certificate }>> => {
    return apiClient.get(`/certificates/detail/${detailId}`);
  },

  // Get certificates by operation code
  getByOperationCode: async (
    code: string,
  ): Promise<ApiResponse<{ certificates: Certificate[] }>> => {
    return apiClient.get(`/certificates/operation/${code}`);
  },

  // Get authenticated user's certificates
  getMyCertificates: async (
    associationId: number,
  ): Promise<ApiResponse<{ certificates: Certificate[] }>> => {
    return apiClient.get("/certificates/my-certificates", {
      params: { associationId },
    });
  },

  // Get projects that support certificates
  getProjects: async (
    associationId: number,
  ): Promise<ApiResponse<{ projects: CertificateProject[] }>> => {
    return apiClient.get("/certificates/projects", {
      params: { associationId },
    });
  },
};

// ==========================================
// Shipping Services
// ==========================================
export const shippingService = {
  // Get all cities for shipping
  getCities: async (): Promise<ApiResponse<{ cities: ShippingCity[] }>> => {
    return apiClient.get("/shipping/cities");
  },

  // Get shipping address for a donation
  getShippingAddress: async (
    operationCode: string,
  ): Promise<ApiResponse<{ address: ShippingAddress | null }>> => {
    return apiClient.get(`/shipping/donation/${operationCode}`);
  },
};
